import React, { createContext, useContext, useEffect, useState } from "react";

import api from "../../services/api";
import { getPublicFileUrl } from "../../config";

const DEFAULT_APP_NAME = "Perabit";
const DEFAULT_APP_TITLE = "Perabit | Gestao Inteligente e CRM WhatsApp";

const BENEFICIOS_PADRAO = [
	{
		titulo: "Todos os canais numa caixa só",
		detalhe:
			"WhatsApp, Facebook Messenger e Instagram Direct chegam na mesma fila.",
	},
	{
		titulo: "Automação que atende sozinha",
		detalhe:
			"Responde na hora, qualifica o cliente e encaminha para o setor certo.",
	},
	{
		titulo: "A equipe sob controle",
		detalhe:
			"Filas por setor, transferências e relatórios de cada atendimento.",
	},
];

const initialMarca = {
	appName: DEFAULT_APP_NAME,
	logoUrl: "",
	backgroundUrl: "",
	primaryColor: "",
	loginTitle: "",
	loginSubtitle: "",
	loginDescription: "",
	loginFeatures: [],
	loginHeroImageUrl: "",
	loginLogoUrl: "",
	faviconUrl: "",
};

const WhiteLabelContext = createContext({
	...initialMarca,
	defaultLoginTitle: "Um só painel.",
	defaultLoginSubtitle: "Controle total do atendimento.",
	defaultLoginDescription:
		"WhatsApp, Facebook e Instagram Direct no mesmo lugar. A automação responde na hora, direciona cada conversa para o setor certo e sua equipe entra só onde precisa fechar a venda.",
	defaultLoginFeatures: BENEFICIOS_PADRAO,
	recarregar: async () => {},
});

// Converte "#38bdf8" no formato que o tema usa nas variáveis CSS: "H S% L%".
const hexParaHsl = (hex) => {
	const limpo = String(hex || "").replace("#", "").trim();

	if (!/^[0-9a-fA-F]{6}$/.test(limpo)) return null;

	const r = parseInt(limpo.slice(0, 2), 16) / 255;
	const g = parseInt(limpo.slice(2, 4), 16) / 255;
	const b = parseInt(limpo.slice(4, 6), 16) / 255;

	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	const l = (max + min) / 2;

	if (max === min) return `0 0% ${Math.round(l * 100)}%`;

	const d = max - min;
	const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

	let h;
	if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
	else if (max === g) h = ((b - r) / d + 2) / 6;
	else h = ((r - g) / d + 4) / 6;

	return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

const contrasteDe = (hsl) => {
	const luminancia = Number(String(hsl).split(" ")[2]?.replace("%", "") || 50);
	return luminancia > 60 ? "222 47% 11%" : "0 0% 100%";
};

const defaultLogin = {
	title: "Um só painel.",
	subtitle: "Controle total do atendimento.",
	description:
		"WhatsApp, Facebook e Instagram Direct no mesmo lugar. A automação responde na hora, direciona cada conversa para o setor certo e sua equipe entra só onde precisa fechar a venda.",
	features: BENEFICIOS_PADRAO,
};

// Garante um link[rel="icon"] no <head> e atualiza o href com a imagem.
const aplicarFavicon = (faviconUrl) => {
	if (typeof document === "undefined") return;

	let link = document.querySelector("link[rel~='icon']");
	if (!link) {
		link = document.createElement("link");
		link.type = "image/x-icon";
		link.rel = "icon";
		document.getElementsByTagName("head")[0].appendChild(link);
	}

	const href = faviconUrl ? getPublicFileUrl(faviconUrl) : "/favicon.ico";
	// Cache buster: se trocar favicon, navegador baixa o novo agora.
	const buster = faviconUrl ? `?_=${Date.now().toString(36)}` : "";
	link.href = `${href}${faviconUrl ? buster : ""}`;
};

export const WhiteLabelProvider = ({ children }) => {
	const [marca, setMarca] = useState(initialMarca);

	const carregar = async () => {
		try {
			const { data } = await api.get("/settings/white-label");
			setMarca({
				appName: data?.appName || DEFAULT_APP_NAME,
				logoUrl: data?.logoUrl || "",
				backgroundUrl: data?.backgroundUrl || "",
				primaryColor: data?.primaryColor || "",
				loginTitle: data?.loginTitle || "",
				loginSubtitle: data?.loginSubtitle || "",
				loginDescription: data?.loginDescription || "",
				loginFeatures: Array.isArray(data?.loginFeatures) ? data.loginFeatures : [],
				loginHeroImageUrl: data?.loginHeroImageUrl || "",
				loginLogoUrl: data?.loginLogoUrl || "",
				faviconUrl: data?.faviconUrl || "",
			});
		} catch (err) {
			// Sem whitelabel o sistema usa o visual padrão — não é motivo para
			// travar a tela.
		}
	};

	useEffect(() => {
		carregar();
	}, []);

	// Aplica no documento: título da aba, cor primária e favicon.
	useEffect(() => {
		const nome = String(marca.appName || DEFAULT_APP_NAME).trim();
		if (nome.length > 0 && nome.toLowerCase() === "whaticket") {
			document.title = DEFAULT_APP_TITLE;
		} else if (nome.toLowerCase().includes("perabit")) {
			document.title = DEFAULT_APP_TITLE;
		} else {
			document.title = nome;
		}

		const hsl = hexParaHsl(marca.primaryColor);

		if (hsl) {
			const raiz = document.documentElement;
			raiz.style.setProperty("--primary", hsl);
			raiz.style.setProperty("--primary-foreground", contrasteDe(hsl));
			raiz.style.setProperty("--ring", hsl);
		}

		aplicarFavicon(marca.faviconUrl || null);
	}, [marca.appName, marca.primaryColor, marca.faviconUrl]);

	const logoCompleta = getPublicFileUrl(marca.logoUrl);
	const fundoCompleto = getPublicFileUrl(marca.backgroundUrl);
	const heroCompleta = getPublicFileUrl(marca.loginHeroImageUrl);
	const loginLogoCompleta = getPublicFileUrl(marca.loginLogoUrl);

	// Se tiver loginLogoUrl definida, logo no login usa ela; senão, cai no
	// logo geral (marca). No contexto expõe separado para decidir na tela.

	const valorContexto = {
		...marca,
		logoUrl: logoCompleta,
		backgroundUrl: fundoCompleto,
		loginHeroImageUrl: heroCompleta,
		loginLogoUrl: loginLogoCompleta,
		defaultLoginTitle: defaultLogin.title,
		defaultLoginSubtitle: defaultLogin.subtitle,
		defaultLoginDescription: defaultLogin.description,
		defaultLoginFeatures: defaultLogin.features,
		recarregar: carregar,
	};

	return (
		<WhiteLabelContext.Provider value={valorContexto}>
			{children}
		</WhiteLabelContext.Provider>
	);
};

export const useWhiteLabel = () => useContext(WhiteLabelContext);

export default WhiteLabelContext;
